import React, { useCallback, useContext, useState } from 'react';

import Navbar from 'components/actionModal/Navbar';
import CorporateAccountDetails from 'components/actionModal/stages/accountDetails/CorporateAccountDetails';
import Result from 'components/actionModal/stages/Result';
import SelectCategory from 'components/actionModal/stages/SelectCategory';
import { ActionModalMainContent } from 'components/actionModal/styledElements/stagesStyledElements';
import { formatServerError } from 'helpers/formatServerError';
import { CreateCorporateAccountPayload } from 'modules/accounts/accountsServices/CorporateAccountsService';

import { CorporateAccountContext } from '../../context/CorporateAccountContext';
import useCreateCorporateAccount from '../../hooks/useCreateCorporateAccount';
import {
  AccountCategory,
  AccountModalWindowStages,
  createCorporateAccountErrorResponse,
} from '../../models';
import { dataForSelectingCategory } from '../CreateAccountModal';

const navbarItemsForCorporate = [
  {
    text: 'Select type',
    name: AccountModalWindowStages.SelectType,
  },
  {
    text: 'Account details',
    name: AccountModalWindowStages.AccountDetails,
  },
];

type CreateCorporateModalProps = {
  isNavbarTitleShown: boolean;
  activatedStages: AccountModalWindowStages[];
  closeModalWindow: () => void;
  showNavbarTitle: (value: boolean) => void;
  setActiveCategory: (category: AccountCategory) => void;
  setActivatedStages: (
    stage: AccountModalWindowStages,
    value?: boolean
  ) => void;
};

const CreateCorporateModal = ({
  activatedStages,
  showNavbarTitle,
  closeModalWindow,
  setActiveCategory,
  isNavbarTitleShown,
  setActivatedStages,
}: CreateCorporateModalProps) => {
  const [completionTitle, setCompletionTitle] = useState('');
  const { groupName, saveGroupName, businessSector, saveBusinessSector } =
    useContext(CorporateAccountContext);
  const [activeStage, setActiveStage] = useState<AccountModalWindowStages>(
    AccountModalWindowStages.SelectType
  );

  const goToResultStage = () => {
    setActiveStage(AccountModalWindowStages.Result);
  };

  const { mutate, error } = useCreateCorporateAccount(
    setCompletionTitle,
    goToResultStage
  );

  const formattedError = formatServerError(
    error,
    createCorporateAccountErrorResponse
  );

  const changeActiveStage = useCallback(
    (stage: number) => setActiveStage(navbarItemsForCorporate[stage].name),
    []
  );

  const goToSelectCategoryStage = useCallback(() => {
    setActiveStage(AccountModalWindowStages.SelectType);
  }, []);

  const goToAccountDetailsStage = useCallback(() => {
    setActiveStage(AccountModalWindowStages.AccountDetails);
    setActivatedStages(AccountModalWindowStages.AccountDetails);
    if (!isNavbarTitleShown) {
      showNavbarTitle(true);
    }
  }, [isNavbarTitleShown, setActivatedStages, showNavbarTitle]);

  const handleCreateAccount = (data: CreateCorporateAccountPayload) => {
    saveGroupName(data.groupName);
    saveBusinessSector(data.businessSector);
    mutate(data);
  };

  return (
    <>
      {activeStage === AccountModalWindowStages.Result ? (
        <Result title={completionTitle} closeModal={closeModalWindow} />
      ) : (
        <>
          <Navbar
            title="Create account"
            activeStage={activeStage}
            showTitle={isNavbarTitleShown}
            accountCategory={AccountCategory.Group}
            activatedStages={activatedStages}
            changeActiveStage={changeActiveStage}
            navbarItems={navbarItemsForCorporate}
          />
          <ActionModalMainContent>
            {activeStage === AccountModalWindowStages.SelectType && (
              <SelectCategory
                saveData={setActiveCategory}
                accountCategory={AccountCategory.Group}
                categories={dataForSelectingCategory}
                goToNextStage={goToAccountDetailsStage}
              />
            )}
            {activeStage === AccountModalWindowStages.AccountDetails && (
              <CorporateAccountDetails
                title="Account details"
                groupName={groupName}
                businessSector={businessSector}
                errorResponse={formattedError}
                goToNextStage={handleCreateAccount}
                goToPreviousStage={goToSelectCategoryStage}
              />
            )}
          </ActionModalMainContent>
        </>
      )}
    </>
  );
};

export default CreateCorporateModal;
